import { motion } from "framer-motion";
import { Phone, ArrowRight, Clock3, ShieldCheck, Tag, MapPin, CalendarCheck2, Truck, HeartHandshake, Recycle } from "lucide-react";
import { EASE } from "./Reveal";

const TRUST = [
  { icon: Clock3, label: "Fast & Reliable" },
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Tag, label: "Upfront Pricing" },
  { icon: MapPin, label: "Locally Owned" },
];

const FEATURES = [
  { icon: CalendarCheck2, title: "Same-Day & Next-Day", sub: "Service Available", slug: "same-day" },
  { icon: Truck, title: "Full Service", sub: "Junk Removal", slug: "full-service" },
  { icon: HeartHandshake, title: "Respectful Of Your", sub: "Home & Property", slug: "respect" },
  { icon: Recycle, title: "Eco-Friendly", sub: "Disposal", slug: "eco" },
];

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: EASE },
});

const HeroBackdrop = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.7, ease: EASE }}
    className="absolute right-0 top-0 hidden aspect-[1.15] h-[calc(100%-13rem)] max-w-[70%] lg:block"
    aria-hidden="true"
  >
    <img
      src="/assets/crew-cover.jpg"
      alt=""
      fetchPriority="high"
      data-testid="hero-crew-image-desktop"
      className="h-full w-full object-cover object-left-top brightness-[1.12] contrast-[1.05]"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-ink from-0% via-ink/50 via-18% to-transparent to-42%" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent via-14% to-ink/25" />
  </motion.div>
);

const HeroHeadline = () => (
  <h2 data-testid="hero-headline" className="font-heading uppercase leading-[0.88] tracking-tight">
    <motion.span
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.15, ease: EASE }}
      className="block text-[clamp(3.8rem,7.5vw,7.5rem)] text-white"
    >
      Junk Gone.
    </motion.span>
    <motion.span
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.3, ease: EASE }}
      className="block text-[clamp(3.8rem,7.5vw,7.5rem)] text-gold"
    >
      Space Reclaimed.
    </motion.span>
  </h2>
);

const HeroActions = () => (
  <motion.div {...fadeUp(0.6)} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
    <a
      href="#contact"
      data-testid="hero-quote-button"
      className="btn-brutal shadow-hard-gold flex items-center justify-center gap-3 bg-gold px-10 py-4 font-heading text-2xl uppercase tracking-wide text-ink"
    >
      Get a Free Quote <ArrowRight size={20} strokeWidth={2.5} />
    </a>
    <a
      href="tel:4706840150"
      data-testid="hero-call-button"
      className="btn-brutal-dark flex items-center justify-center gap-3 border-2 border-white bg-ink/40 px-10 py-4 font-heading text-2xl uppercase tracking-wide text-white"
    >
      <Phone size={20} className="text-gold" />
      Call 470-684-0150
    </a>
  </motion.div>
);

const TrustRow = () => (
  <motion.div {...fadeUp(0.75)} data-testid="hero-trust-row" className="mt-9 flex items-center">
    {TRUST.map((t, i) => (
      <span key={t.label} className={`flex items-center gap-2 ${i > 0 ? "ml-4 border-l-2 border-gold/30 pl-4" : ""}`}>
        <t.icon size={15} strokeWidth={1.8} className="text-gold" />
        <span className="whitespace-nowrap font-body text-xs font-bold uppercase tracking-[0.1em] text-white/80">{t.label}</span>
      </span>
    ))}
  </motion.div>
);

const HeroCopy = () => (
  <div className="max-w-2xl pb-10 pt-32 md:pt-40 lg:pt-36">
    <h1 data-testid="hero-eyebrow" className="sr-only">
      Junk Removal & Hauling in Kennesaw, GA
    </h1>
    <HeroHeadline />
    <motion.p
      data-testid="hero-subhead"
      {...fadeUp(0.45)}
      className="mt-6 max-w-xl text-base font-medium capitalize leading-relaxed text-white/85 md:text-lg"
    >
      Premium Junk Removal & Hauling Across Metro Atlanta & Surrounding Areas — Done With Care, Professionalism,
      and Zero Stress. From Single-Item Pickups to Complete Property Cleanouts, Godspeed Handles Every Detail and
      Leaves Your Space Clean, Clear, and Ready for What’s Next!
    </motion.p>
    <HeroActions />
    <TrustRow />
  </div>
);

const MobileCrewImage = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.6, ease: EASE }}
    className="relative z-10 -mx-5 md:-mx-8 lg:hidden"
  >
    <img
      src="/assets/crew-cover.jpg"
      alt="GODSPEED junk removal crew in branded Built On Faith shirts working at a Kennesaw, Georgia home"
      data-testid="hero-crew-image-mobile"
      className="block h-auto w-full"
    />
  </motion.div>
);

const FaithCard = () => (
  <div data-testid="hero-faith-card" className="flex items-center gap-5 border-2 border-gold/60 bg-ink/95 p-6 lg:col-span-4">
    <img src="/assets/cross-icon.png" alt="Godspeed cross and wings emblem" className="h-16 w-16 shrink-0 object-contain" />
    <div>
      <p className="font-heading text-2xl uppercase leading-tight tracking-wide text-gold">Built On Faith • Built To Last</p>
      <p className="mt-2 font-body text-[11px] font-semibold uppercase leading-relaxed tracking-[0.12em] text-white/75">
        Commit to the Lord whatever you do, and He will establish your plans.
      </p>
      <p className="mt-1 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-gold">– Proverbs 16:3</p>
    </div>
  </div>
);

const FeaturesCard = () => (
  <div data-testid="hero-features-card" className="grid grid-cols-2 border-2 border-coal bg-ink/95 lg:col-span-8 lg:grid-cols-4">
    {FEATURES.map((f, i) => (
      <div
        key={f.slug}
        data-testid={`hero-feature-${f.slug}`}
        className={`flex flex-col items-center gap-3 px-4 py-6 text-center ${i % 2 === 1 ? "border-l-2 border-coal" : ""} ${i > 1 ? "border-t-2 border-coal lg:border-t-0" : ""} ${i >= 1 ? "lg:border-l-2" : ""}`}
      >
        <f.icon size={26} strokeWidth={1.5} className="text-gold" />
        <span>
          <span className="block font-body text-[11px] font-bold uppercase leading-snug tracking-[0.12em] text-white md:text-xs">{f.title}</span>
          <span className="mt-0.5 block font-body text-[11px] font-bold uppercase leading-snug tracking-[0.12em] text-white/55 md:text-xs">{f.sub}</span>
        </span>
      </div>
    ))}
  </div>
);

const HeroCards = () => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.9, ease: EASE }}
    className="grid grid-cols-1 gap-4 pb-12 pt-8 lg:grid-cols-12 lg:gap-5 lg:pt-0"
  >
    <FaithCard />
    <FeaturesCard />
  </motion.div>
);

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-ink">
    <HeroBackdrop />
    <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8">
      <HeroCopy />
      <MobileCrewImage />
      <HeroCards />
    </div>
    <div className="hazard relative z-10 h-3 w-full" aria-hidden="true" />
  </section>
);
